import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import classes from './TypesOfFiller.module.css';
import Placeholder from '../../UI/Placeholder/Placeholder';
import SettingsForm from '../SettingsForm/SettingsForm';
import useForm from '../../../hooks/useForm';
import SettingsList from '../SettingsList/SettingsList';
import { AlertType, FillerType } from '../../../types/common';
import Card from '../../UI/Card/Card';
import { createFillerType, deleteFillerType, removeSelectedFillerType, selectFillerType, updateFillerType } from '../../../store/FillerTypeSlice';
import IconButton from '../../UI/IconButton/IconButton';
import AddIcon from '../../UI/icons/AddIcon/AddIcon';
import { showAlert } from '../../../store/CommonSlice';
import { DELETE_FILLERTYPE_ALERT_MESSAGE, NO_TYPESOFFILLER_MESSAGE } from '../../../constants/messages';
import AreaLoader from '../../UI/AreaLoader/AreaLoader';
import { updateAllProductsTypesOfFiller } from '../../../store/ProductSlice';
import { fillerTypeFormValidator } from '../../../utils/validators';

const INIT_INPUT = {
    name: '',
    description: '',
    url: '',
};

const TypesOfFiller: React.FC = () => {
    const { isLoading, typesOfFiller, error } = useSelector((state: RootState) => state.fillerType);
    const { products } = useSelector((state: RootState) => state.product);
    const fillerTypeToBeEdited = useSelector((state: RootState) => state.fillerType.selectedFillerType);
    const dispatch = useDispatch<AppDispatch>();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const { input, setInput, handleChange, errors, submit, resetForm } = useForm<
        typeof INIT_INPUT,
        string | { [key: string]: string } // Уточняем тип inputValue
    >(
        INIT_INPUT,
        handleSubmit,
        fillerTypeFormValidator, // Теперь сигнатуры совпадают
    );
    const isPlaceholderVisible = !isLoading && !error.isError && !isFormOpen && typesOfFiller?.length === 0;

    const modifedTypesOfFiller = typesOfFiller.map((fillerType) => {
        const productCount = products.filter((product) => product.fillerType?.id === fillerType.id).length;
        const discountedProductsCount = products.filter((product) => product.fillerType?.id === fillerType.id && product.discount).length;

        return {
            ...fillerType,
            productsCount: productCount,
            discountedProductsCount,
        };
    });

    useEffect(() => {
        if (!fillerTypeToBeEdited.id) return;

        setInput((prev) => ({
            ...prev,
            ...fillerTypeToBeEdited,
        }));
    }, [fillerTypeToBeEdited, setInput]);

    function handleSubmit() {
        const name = input.name.trim();
        const description = input.description.trim();
        const url = input.url.trim();

        if (fillerTypeToBeEdited.id) {
            const updatedFillerType = {
                ...fillerTypeToBeEdited,
                ...input,
                url,
            };
            dispatch(updateAllProductsTypesOfFiller(updatedFillerType));
            dispatch(updateFillerType(updatedFillerType));
        } else {
            const newFillerType = {
                name,
                description,
                url,
            };

            dispatch(createFillerType(newFillerType));
        }

        closeForm();
    }

    const openForm = () => {
        setIsFormOpen(true);
    };

    const closeForm = () => {
        setIsFormOpen(false);
        dispatch(removeSelectedFillerType());
        resetForm();
    };

    const deleteFillerTypeHendler = async (id: FillerType['id']) => {
        const hasProducts = modifedTypesOfFiller.some((fillerType) => fillerType.id === id && fillerType.productsCount > 0);

        if (hasProducts) {
            dispatch(showAlert({ type: AlertType.Error, message: DELETE_FILLERTYPE_ALERT_MESSAGE }));

            return;
        }
        dispatch(deleteFillerType(id));
    };

    const editFillerTypeHandler = (id: FillerType['id']) => {
        dispatch(selectFillerType(id));
        openForm();
    };

    return (
        <Card>
            <div className={classes.fillerType}>
                <div className={classes.header}>
                    <h3 className={classes.title}>Типы наполнителя</h3>

                    <IconButton onClick={openForm} isDisabled={isFormOpen || error.isError}>
                        <AddIcon />
                    </IconButton>
                </div>

                {isLoading && <AreaLoader />}

                {!isLoading && error.isError && !isFormOpen && <Placeholder text={error.message} size={'32px'} />}

                {isPlaceholderVisible && !error.isError && <Placeholder text={NO_TYPESOFFILLER_MESSAGE} size={'32px'} />}

                {isFormOpen && (
                    <SettingsForm
                        onClose={closeForm}
                        onChange={handleChange}
                        onSubmit={submit}
                        errors={errors}
                        value={input}
                        labelName={'Название фильтра'}
                        labelURL={'SEO URL'}
                        namePlaceholder={'Укажите название фильтра'}
                        descriptionPlaceholder={'Описание фильтра'}
                    />
                )}

                {!isFormOpen && typesOfFiller?.length > 0 && (
                    <SettingsList items={modifedTypesOfFiller} onEdit={editFillerTypeHandler} onDelete={deleteFillerTypeHendler} />
                )}
            </div>
        </Card>
    );
};

export default TypesOfFiller;
